import type {
  AdminAuthModeResponse,
  AdminSessionResponse,
  AutoDormCheckIn,
  AutoDormCheckSettings,
  AutoDormCheckTask,
  AutoDormCheckUser,
  AuditLevel,
  AuditResult,
  CalendarRecord,
  CreatePublicationInput,
  JobState,
  Overview,
  Paginated,
  PublicationDetail,
  PublicationMedia,
  PublicationRecipient,
  PublicationSummary,
  SnapshotResource,
  SnapshotOwner,
  SnapshotRow,
  StorageOverview,
  UserDetail,
  UserMajor,
  UserSummary,
} from "./types";

const ADMIN_ROOT = "/api/v1/admin";
const ADMIN_REQUEST_HEADER = "easy-swu-admin";

interface Envelope<T> {
  success: true;
  data: T;
}

interface ErrorEnvelope {
  success: false;
  error?: { code?: string; message?: string; details?: unknown };
  requestId?: string;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code = "REQUEST_FAILED",
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(
  path: string,
  options: RequestInit & { suppressUnauthorizedEvent?: boolean } = {},
): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set("Accept", "application/json");
  headers.set("X-Admin-Request", ADMIN_REQUEST_HEADER);
  const binaryBody = options.body instanceof Blob;
  if (
    options.body &&
    !(options.body instanceof FormData) &&
    !binaryBody &&
    !headers.has("Content-Type")
  ) {
    headers.set("Content-Type", "application/json");
  }
  if (binaryBody && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/octet-stream");
  }
  const response = await fetch(`${ADMIN_ROOT}${path}`, {
    ...options,
    credentials: "include",
    headers,
  });
  const payload = (await response.json().catch(() => null)) as
    Envelope<T> | ErrorEnvelope | null;
  if (!response.ok || !payload || !payload.success) {
    const failure = payload as ErrorEnvelope | null;
    if (response.status === 401 && !options.suppressUnauthorizedEvent) {
      window.dispatchEvent(new CustomEvent("easy-swu:admin-unauthorized"));
    }
    throw new ApiError(
      failure?.error?.message || `请求失败（HTTP ${response.status}）`,
      response.status,
      failure?.error?.code,
      failure?.requestId,
    );
  }
  return payload.data;
}

function query(values: Record<string, string | number | undefined>): string {
  const params = new URLSearchParams();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== "") params.set(key, String(value));
  });
  const encoded = params.toString();
  return encoded ? `?${encoded}` : "";
}

export const api = {
  authMode() {
    return request<AdminAuthModeResponse>("/auth-mode", {
      suppressUnauthorizedEvent: true,
    });
  },
  login(username: string, password: string) {
    return request<AdminSessionResponse>("/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
      suppressUnauthorizedEvent: true,
    });
  },
  logout() {
    return request<{ loggedOut: true }>("/logout", {
      method: "POST",
      suppressUnauthorizedEvent: true,
    });
  },
  ssoLoginUrl({ forceLogin = false }: { forceLogin?: boolean } = {}) {
    return `${ADMIN_ROOT}/oidc/login${forceLogin ? "?prompt=login" : ""}`;
  },
  ssoLogoutUrl() {
    return `${ADMIN_ROOT}/oidc/logout`;
  },
  session() {
    return request<AdminSessionResponse>("/session", {
      suppressUnauthorizedEvent: true,
    });
  },
  overview() {
    return request<Overview>("/overview");
  },
  users(options: { page?: number; pageSize?: number; q?: string } = {}) {
    return request<Paginated<UserSummary>>(`/users${query(options)}`);
  },
  userMajors() {
    return request<UserMajor[]>("/users/majors");
  },
  user(id: string) {
    return request<UserDetail>(`/users/${encodeURIComponent(id)}`);
  },
  autoDormCheckSettings() {
    return request<AutoDormCheckSettings>("/auto-dorm-check/settings");
  },
  updateAutoDormCheckSettings(
    input: Partial<
      Pick<
        AutoDormCheckSettings,
        | "entryEnabled"
        | "functionEnabled"
        | "checkInStartTime"
        | "checkInEndTime"
      >
    >,
  ) {
    return request<AutoDormCheckSettings>("/auto-dorm-check/settings", {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  },
  autoDormCheckUsers(options: { page?: number; pageSize?: number } = {}) {
    return request<Paginated<AutoDormCheckUser>>(
      `/auto-dorm-check/users${query(options)}`,
    );
  },
  autoDormCheckTasks(
    userId: string,
    options: { page?: number; pageSize?: number } = {},
  ) {
    return request<Paginated<AutoDormCheckTask>>(
      `/auto-dorm-check/users/${encodeURIComponent(userId)}/tasks${query(options)}`,
    );
  },
  autoDormCheckIns(
    userId: string,
    options: { page?: number; pageSize?: number } = {},
  ) {
    return request<Paginated<AutoDormCheckIn>>(
      `/auto-dorm-check/users/${encodeURIComponent(userId)}/check-ins${query(options)}`,
    );
  },
  setUserActivation(id: string, active: boolean) {
    return request<{
      accountActive: boolean;
      accountDeactivatedAt: string | null;
      accountDeactivationReason: string | null;
      revokedSessions: number;
    }>(`/users/${encodeURIComponent(id)}/activation`, {
      method: "PATCH",
      body: JSON.stringify({ active }),
    });
  },
  refreshUser(id: string) {
    return request<{
      resources: Array<{ resource: string; status: "updated" | "failed" }>;
    }>(`/users/${encodeURIComponent(id)}/refresh`, { method: "POST" });
  },
  revokeUserSessions(id: string) {
    return request<{ revoked: number }>(
      `/users/${encodeURIComponent(id)}/revoke-sessions`,
      { method: "POST" },
    );
  },
  revokeUserSession(id: string, sessionId: string) {
    return request<{ revoked: true }>(
      `/users/${encodeURIComponent(id)}/sessions/${encodeURIComponent(sessionId)}`,
      { method: "DELETE" },
    );
  },
  clearElectricityBinding(id: string) {
    return request<{ deleted: true }>(
      `/users/${encodeURIComponent(id)}/electricity-binding`,
      { method: "DELETE" },
    );
  },
  deleteCourseAssistantReview(userId: string, reviewId: string) {
    return request<{ deleted: true }>(
      `/users/${encodeURIComponent(userId)}/course-assistant/reviews/${encodeURIComponent(reviewId)}`,
      { method: "DELETE" },
    );
  },
  deleteUser(id: string, account: string) {
    return request<{ deleted: boolean }>(
      `/users/${encodeURIComponent(id)}${query({ confirmAccount: account })}`,
      { method: "DELETE" },
    );
  },
  snapshotResources() {
    return request<SnapshotResource[]>("/snapshots/resources");
  },
  snapshotOwners(
    options: {
      page?: number;
      pageSize?: number;
      q?: string;
      resource?: string;
    } = {},
  ) {
    return request<Paginated<SnapshotOwner>>(
      `/snapshot-owners${query(options)}`,
    );
  },
  snapshots(
    options: {
      page?: number;
      pageSize?: number;
      q?: string;
      resource?: string;
    } = {},
  ) {
    return request<Paginated<SnapshotRow>>(`/snapshots${query(options)}`);
  },
  deleteSnapshot(userId: string, resourceKey: string) {
    return request<{ deleted: boolean }>(
      `/snapshots/${encodeURIComponent(userId)}/${encodeURIComponent(resourceKey)}?confirm=true`,
      { method: "DELETE" },
    );
  },
  jobs() {
    return request<JobState[]>("/jobs");
  },
  runJob(name: string) {
    return request<{
      accepted: boolean;
      reason?: "already_running";
      name: string;
    }>(`/jobs/${encodeURIComponent(name)}/run`, { method: "POST" });
  },
  calendars() {
    return request<CalendarRecord[]>("/calendars");
  },
  storage() {
    return request<StorageOverview>("/storage");
  },
  publications(
    options: {
      page?: number;
      pageSize?: number;
      q?: string;
      status?: "current" | "deleted" | "all";
      kind?: "announcement" | "notification";
    } = {},
  ) {
    return request<Paginated<PublicationSummary>>(
      `/publications${query(options)}`,
    );
  },
  publication(id: string) {
    return request<PublicationDetail>(
      `/publications/${encodeURIComponent(id)}`,
    );
  },
  publicationRecipients(
    id: string,
    options: { page?: number; pageSize?: number } = {},
  ) {
    return request<Paginated<PublicationRecipient>>(
      `/publications/${encodeURIComponent(id)}/recipients${query(options)}`,
    );
  },
  createPublication(input: CreatePublicationInput) {
    return request<PublicationDetail>("/publications", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  deletePublication(id: string) {
    return request<{ deleted: true }>(
      `/publications/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    );
  },
  restorePublication(id: string) {
    return request<{ restored: true }>(
      `/publications/${encodeURIComponent(id)}/restore`,
      { method: "POST" },
    );
  },
  uploadPublicationImage(file: File) {
    return request<PublicationMedia>("/media", {
      method: "POST",
      headers: { "X-File-Name": encodeURIComponent(file.name) },
      body: file,
    });
  },
  audit(
    options: {
      page?: number;
      pageSize?: number;
      q?: string;
      level?: AuditLevel;
    } = {},
  ) {
    return request<AuditResult>(`/audit${query(options)}`);
  },
};
